import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';

// BPMN Status Constants
export const BPMN_STATUS = {
  NOT_DEPLOYED: 0,
  DEPLOYED: 1,
} as const;

export const BPMN_STATUS_LIST = [
  [BPMN_STATUS.NOT_DEPLOYED, 'Not Deployed'],
  [BPMN_STATUS.DEPLOYED, 'Deployed'],
] as const;

// BPMN Item Interface
export interface BpmnItem {
  id: string;
  definitionId: string;
  deploymentId: string;
  name: string;
  fileName: string;
  version: number;
  status: number; // 0: Not Deployed, 1: Deployed
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

// Search Configuration
export const BPMN_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'processName',
    label: 'bpmn.search.process_name',
    type: 'text',
    placeholder: 'bpmn.search.placeholders.process_name',
    showClear: true,
  },
];

// ============================================================================
// TABLE COLUMN CONFIGURATION
// ============================================================================

/**
 * Default column property names (shown on first load)
 * Update these constants to change column property names
 */
export const BPMN_DEFAULT_COLUMNS = {
  ID: 'id',
  NAME: 'name',
  VERSION: 'version',
  STATUS: 'status',
  CREATED_AT: 'createdAt',
  UPDATED_AT: 'updatedAt',
} as const;

/**
 * Flexible column property names (can be shown/hidden via column selector)
 * Update these constants to change flexible column property names
 */
export const BPMN_FLEXIBLE_COLUMNS = {
  PROCESS_FILE_NAME: 'fileName',
} as const;

/**
 * Action column property name
 */
export const BPMN_ACTION_COLUMN = {
  ACTIONS: 'actions',
} as const;

/**
 * Default columns order (shown on first load)
 * Update this array to change the default column order
 */
export const BPMN_DEFAULT_COLUMNS_ORDER: string[] = [
  BPMN_DEFAULT_COLUMNS.ID,
  BPMN_DEFAULT_COLUMNS.NAME,
  BPMN_DEFAULT_COLUMNS.VERSION,
  BPMN_DEFAULT_COLUMNS.STATUS,
  BPMN_DEFAULT_COLUMNS.CREATED_AT,
  BPMN_DEFAULT_COLUMNS.UPDATED_AT,
];

/**
 * Flexible columns array (can be shown/hidden via column selector)
 * Update this array to change which columns are flexible
 */
export const BPMN_FLEXIBLE_COLUMNS_ORDER: string[] = [
  BPMN_FLEXIBLE_COLUMNS.PROCESS_FILE_NAME,
];

// Table Columns Configuration
export const BPMN_COLUMNS: TableColumn[] = [
  {
    label: 'bpmn.table.id',
    prop: BPMN_DEFAULT_COLUMNS.ID,
    width: 100,
    align: 'center',
    type: 'text',
  },
  {
    label: 'bpmn.table.name',
    prop: BPMN_DEFAULT_COLUMNS.NAME,
    width: 250,
    align: 'left',
    type: 'text',
  },
  {
    label: 'bpmn.table.version',
    prop: BPMN_DEFAULT_COLUMNS.VERSION,
    width: 100,
    align: 'center',
    type: 'text',
  },
  {
    label: 'bpmn.table.status',
    prop: BPMN_DEFAULT_COLUMNS.STATUS,
    width: 150,
    align: 'center',
    type: 'status',
  },
  {
    label: 'bpmn.table.created_at',
    prop: BPMN_DEFAULT_COLUMNS.CREATED_AT,
    width: 120,
    align: 'left',
    type: 'custom',
    template: 'dateTemplate',
  },
  {
    label: 'bpmn.table.updated_at',
    prop: BPMN_DEFAULT_COLUMNS.UPDATED_AT,
    width: 120,
    align: 'left',
    type: 'custom',
    template: 'dateTemplate',
  },
  {
    label: 'bpmn.table.process_file_name',
    prop: BPMN_FLEXIBLE_COLUMNS.PROCESS_FILE_NAME,
    width: 200,
    align: 'left',
  },
  {
    label: 'bpmn.table.actions',
    prop: BPMN_ACTION_COLUMN.ACTIONS,
    width: 300,
    align: 'center',
    type: 'actions',
  },
];

// Mock Data for Development
export const MOCK_BPMN_DATA: BpmnItem[] = [
  {
    id: '1',
    definitionId: 'Process_BJU7UN8LF5',
    deploymentId: '',
    name: 'Order Approval Process',
    fileName: 'Process_BJU7UN8LF5.bpmn',
    version: 1,
    status: 0,
    createdAt: '2024-01-15T10:30:00Z',
    updatedAt: '2024-01-15T10:30:00Z',
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: '2',
    definitionId: 'Process_K9M2XN4QRT',
    deploymentId: 'deploy-20240116-091500',
    name: 'Inventory Check Process',
    fileName: 'Process_K9M2XN4QRT.bpmn',
    version: 2,
    status: 1,
    createdAt: '2024-01-14T14:20:00Z',
    updatedAt: '2024-01-16T09:15:00Z',
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: '3',
    definitionId: 'Process_P8L5WZ3MGH',
    deploymentId: '',
    name: 'Refund Process',
    fileName: 'Process_P8L5WZ3MGH.bpmn',
    version: 1,
    status: 0,
    createdAt: '2024-01-13T16:45:00Z',
    updatedAt: '2024-01-13T16:45:00Z',
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: '4',
    definitionId: 'Process_V2N7CX9BDF',
    deploymentId: 'deploy-20240117-133000',
    name: 'Shipping Process',
    fileName: 'Process_V2N7CX9BDF.bpmn',
    version: 3,
    status: 1,
    createdAt: '2024-01-12T11:20:00Z',
    updatedAt: '2024-01-17T13:30:00Z',
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: '5',
    definitionId: 'Process_A6H4Y8QWE1',
    deploymentId: '',
    name: 'Customer Support Process',
    fileName: 'Process_A6H4Y8QWE1.bpmn',
    version: 1,
    status: 0,
    createdAt: '2024-01-11T08:15:00Z',
    updatedAt: '2024-01-11T08:15:00Z',
    createdBy: 'admin',
    updatedBy: 'admin',
  },
];
